/**
 * Arable field cache helper functions for React Query.
 * Service functions for cache operations outside of React components.
 */

import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { mixpanel } from "common/analytics";
import { queryKeys } from "common/queryKeys";
import API from "common/utils/Api";
import {
  ArableField,
  ArableFieldResponse,
  processArableFieldResponse,
} from "model/arableFieldTypes";
import { invalidateArableFieldData } from "services";

const arableFieldApi = new API<ArableFieldResponse>("fields");

/**
 * Get arable field IDs by farm ID.
 * @param farmId - The farm ID
 * @returns Promise resolving to array of arable field IDs
 */
export async function getArableFieldIdsByFarmId(
  farmId: string
): Promise<string[]> {
  return await arableFieldApi.getIds(
    new URLSearchParams({
      farmId: farmId,
      fieldType: "ARABLE,FALLOW",
    })
  );
}

/**
 * Fetch arable fields by farm ID using React Query.
 * Gets field IDs for the farm, then fetches all those fields.
 * @param farmId - The farm ID to fetch arable fields for
 * @param queryClient - The React Query client instance
 * @returns Promise resolving to array of ArableFieldResponse
 */
export async function fetchArableFieldsByFarmIdQuery(
  farmId: string,
  queryClient: QueryClient
): Promise<Array<ArableFieldResponse>> {
  // Get arable field IDs for this farm
  const ids = await getArableFieldIdsByFarmId(farmId);

  if (ids.length > 0) {
    // Fetch all arable fields by their IDs
    return await fetchArableFieldsByIdQuery(ids, queryClient);
  }

  return [];
}

/**
 * Fetch multiple arable fields by their IDs and update React Query cache.
 * @param arableFieldIds - Array of arable field IDs to fetch
 * @param queryClient - React Query client instance
 * @returns Promise resolving to array of ArableFieldResponse
 */
export async function fetchArableFieldsByIdQuery(
  arableFieldIds: Array<string>,
  queryClient: QueryClient
): Promise<Array<ArableFieldResponse>> {
  if (!arableFieldIds || arableFieldIds.length === 0) {
    return [];
  }

  // Fetch all fields using the API
  const responses = await arableFieldApi.getMany(arableFieldIds);

  // Process the responses
  const processedFields = processArableFieldResponse(responses);

  // Update React Query cache for each field and handle validation
  processedFields.forEach((field) => {
    // Check if field was previously invalidated
    const cachedField = queryClient.getQueryData<ArableField>(
      queryKeys.arableFields.detail(field.guid)
    );

    // If field has tiles and was invalidated, validate it
    if (
      cachedField?.invalidatedTimestamp &&
      field.tiles &&
      field.tiles.length > 0
    ) {
      mixpanel.track("ArableField validated", { guid: field.guid });
      const {
        invalidatedTimestamp: _invalidatedTimestamp,
        ...fieldWithoutInvalidated
      } = field;
      queryClient.setQueryData<ArableField>(
        queryKeys.arableFields.detail(field.guid),
        fieldWithoutInvalidated as ArableField
      );
    } else {
      // Update cache with latest data
      queryClient.setQueryData<ArableField>(
        queryKeys.arableFields.detail(field.guid),
        field
      );
    }
  });

  // Return raw responses to match standard behavior
  return responses;
}

/**
 * Hook wrapper for fetchArableFieldsByIdQuery that automatically provides QueryClient
 */
export function useFetchArableFieldsByIdQuery() {
  const queryClient = useQueryClient();
  return async (arableFieldIds: Array<string>) =>
    fetchArableFieldsByIdQuery(arableFieldIds, queryClient);
}

/**
 * Change arable field visibility in React Query cache.
 * @param update - Object with guid and visibility properties
 * @param queryClient - React Query client instance
 */
export function changeArableFieldVisibilityQuery(
  update: {
    guid: string;
    visibility: "none" | "visible";
  },
  queryClient: QueryClient
): void {
  // Get current cached arable field data
  const currentField = queryClient.getQueryData<ArableField>(
    queryKeys.arableFields.detail(update.guid)
  );

  if (currentField) {
    // Update visibility in cached field
    queryClient.setQueryData<ArableField>(
      queryKeys.arableFields.detail(update.guid),
      {
        ...currentField,
        visibility: update.visibility,
      }
    );
  }
}

/**
 * Hook wrapper for changeArableFieldVisibilityQuery that automatically provides QueryClient
 */
export function useChangeArableFieldVisibilityQuery() {
  const queryClient = useQueryClient();
  return (update: { guid: string; visibility: "none" | "visible" }) =>
    changeArableFieldVisibilityQuery(update, queryClient);
}

/**
 * Invalidate arable field data in React Query cache (mark field as needing re-fetch).
 * Sets invalidatedTimestamp, clears tiles array, and triggers a refetch.
 * @param arableFieldGuid - The arable field guid to invalidate
 * @param queryClient - React Query client instance
 */
export function invalidateArableFieldDataQuery(
  arableFieldGuid: string,
  queryClient: QueryClient
): void {
  invalidateArableFieldData(arableFieldGuid, queryClient);
}

/**
 * Hook wrapper for invalidateArableFieldDataQuery that automatically provides QueryClient
 */
export function useInvalidateArableFieldDataQuery() {
  const queryClient = useQueryClient();
  return (arableFieldGuid: string) =>
    invalidateArableFieldDataQuery(arableFieldGuid, queryClient);
}

/**
 * Validate arable field data in React Query cache (remove invalidatedTimestamp).
 * @param arableFieldGuid - The arable field guid to validate
 * @param queryClient - React Query client instance
 */
export function validateArableFieldDataQuery(
  arableFieldGuid: string,
  queryClient: QueryClient
): void {
  mixpanel.track("ArableField validated", { guid: arableFieldGuid }); // stop the timer
  mixpanel.track("ArableField validation timeout", { guid: arableFieldGuid });
  // Get current cached arable field data
  const currentField = queryClient.getQueryData<ArableField>(
    queryKeys.arableFields.detail(arableFieldGuid)
  );

  if (currentField) {
    // Remove invalidatedTimestamp from cached field
    const {
      invalidatedTimestamp: _invalidatedTimestamp,
      ...fieldWithoutInvalidated
    } = currentField;
    queryClient.setQueryData<ArableField>(
      queryKeys.arableFields.detail(arableFieldGuid),
      fieldWithoutInvalidated as ArableField
    );
  }
}

/**
 * Hook wrapper for validateArableFieldDataQuery that automatically provides QueryClient
 */
export function useValidateArableFieldDataQuery() {
  const queryClient = useQueryClient();
  return (arableFieldGuid: string) =>
    validateArableFieldDataQuery(arableFieldGuid, queryClient);
}

/**
 * Reset all arable fields from React Query cache.
 * Removes all arable field-related queries from the cache.
 * @param queryClient - React Query client instance
 */
export function resetArableFieldsQueryCache(queryClient: QueryClient): void {
  // Remove all arable field-related queries from cache
  // With hierarchical keys, this removes all queries starting with ["arableFields"]
  queryClient.removeQueries({ queryKey: queryKeys.arableFields.all });
}

/**
 * Hook wrapper for resetArableFieldsQueryCache that automatically provides QueryClient
 */
export function useResetArableFieldsQueryCache() {
  const queryClient = useQueryClient();
  return () => resetArableFieldsQueryCache(queryClient);
}
